import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { merchandiseAPI, uploadAPI, resolveImage , translationAPI} from '../utils/api';
import { tField } from '../utils/bilingualHelper';
import { useTranslation } from 'react-i18next';

export default function Merchandise() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [translated, setTranslated] = useState(false);
  const [translating, setTranslating] = useState(false);

  const [formData, setFormData] = useState({
    nameEn: '',
    nameZh: '',
    price: '',
    gender: 'Unisex',
    sizes: [],
    imageUrl: '',
    photoFile: null,
    stock: '',
    inStock: true,
    descriptionEn: '',
    descriptionZh: ''
  });

  const fetchIdRef = React.useRef(0);

  const fetchItems = async (isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    try {
      const res = await merchandiseAPI.getAll();
      if (!isMounted || currentId !== fetchIdRef.current) return;
      if (res.data.success) {
        setItems(res.data.items);
      }
    } catch (err) {
      if (!isMounted || currentId !== fetchIdRef.current) return;
      alert(t('merchandise.errLoad'));
    } finally {
      if (isMounted && currentId === fetchIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    fetchItems(isMounted);
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      // Handle both legacy string names and new {en,zh} objects
      const nameEn = typeof item.name === 'object' ? (item.name.en || '') : (item.name || '');
      const nameZh = typeof item.name === 'object' ? (item.name.zh || '') : '';
      const descEn = typeof item.description === 'object' ? (item.description.en || '') : (item.description || '');
      const descZh = typeof item.description === 'object' ? (item.description.zh || '') : '';
      setFormData({
        nameEn,
        nameZh,
        price: item.price,
        gender: item.gender || 'Unisex',
        sizes: item.sizes || [],
        imageUrl: item.imageUrl,
        photoFile: null,
        stock: item.stock || 0,
        inStock: item.inStock !== undefined ? item.inStock : true,
        descriptionEn: descEn,
        descriptionZh: descZh
      });
    } else {
      setEditingItem(null);
      setFormData({ nameEn: '', nameZh: '', price: '', gender: 'Unisex', sizes: [], imageUrl: '', photoFile: null, stock: '', inStock: true, descriptionEn: '', descriptionZh: '' });
    }
    setTranslated(item && item.name?.zh ? true : false);
    setIsModalOpen(true);
  };

  const handleTranslateAll = async () => {
    const fieldsToTranslate = [
      { en: formData.nameEn, zh: formData.nameZh, key: 'name' },
      { en: formData.descriptionEn, zh: formData.descriptionZh, key: 'description' }
    ];
    
    // Check if at least one field is filled in either language
    const hasData = fieldsToTranslate.some(f => f.en?.trim() || f.zh?.trim());
    if (!hasData) {
      return alert('Please fill in at least one name or description field before translating.');
    }

    setTranslating(true);
    try {
      const promises = fieldsToTranslate.map(f => {
        if (!f.en?.trim() && !f.zh?.trim()) return Promise.resolve(null);
        
        const enStr = f.en?.trim();
        const zhStr = f.zh?.trim();
        
        if (zhStr && !enStr) {
          return translationAPI.translate(zhStr, 'zh-TW', 'en')
            .then(d => ({ key: f.key, en: d.responseData?.translatedText || '' }))
            .catch(() => null);
        } else {
          return translationAPI.translate(enStr, 'en', 'zh-TW')
            .then(d => ({ key: f.key, zh: d.responseData?.translatedText || '' }))
            .catch(() => null);
        }
      });
      
      const results = await Promise.all(promises);
      const updates = {};
      results.forEach(res => {
         if (!res) return;
         if (res.key === 'name') {
           if (res.zh) updates.nameZh = res.zh;
           if (res.en) updates.nameEn = res.en;
         }
         if (res.key === 'description') {
           if (res.zh) updates.descriptionZh = res.zh;
           if (res.en) updates.descriptionEn = res.en;
         }
      });
      
      setFormData(p => ({ ...p, ...updates }));
      setTranslated(true);
    } catch(err) {
      alert('Translation failed. Please try manually.');
    } finally {
      setTranslating(false);
    }
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({ ...formData, photoFile: file, imageUrl: URL.createObjectURL(file) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nameEn || !formData.price) {
      alert(t('merchandise.reqNamePrice'));
      return;
    }
    if (!formData.nameZh) {
      alert('Chinese translation for Name is missing.');
      return;
    }

    try {
      let finalImageUrl = formData.imageUrl;
      if (formData.photoFile) {
        const uploadData = new FormData();
        uploadData.append('photo', formData.photoFile);
        const uploadRes = await uploadAPI.merchandiseImage(uploadData);
        if (uploadRes.data.success) {
          finalImageUrl = uploadRes.data.url;
        }
      }

      const payload = {
        name: { en: formData.nameEn, zh: formData.nameZh },
        price: Number(formData.price),
        gender: formData.gender,
        sizes: formData.sizes,
        imageUrl: finalImageUrl,
        stock: Number(formData.stock) || 0,
        inStock: formData.inStock,
        description: { en: formData.descriptionEn, zh: formData.descriptionZh }
      };

      if (editingItem) {
        await merchandiseAPI.update(editingItem._id || editingItem.id, payload);
        alert(t('merchandise.updated'));
      } else {
        await merchandiseAPI.create(payload);
        alert(t('merchandise.created'));
      }

      setIsModalOpen(false);
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.message || t('merchandise.errSave'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('merchandise.deleteConfirm'))) return;
    try {
      await merchandiseAPI.delete(id);
      alert(t('merchandise.deleted'));
      fetchItems();
    } catch (err) {
      alert(t('merchandise.errDelete'));
    }
  };

  if (loading) return <Layout title={t('merchandise.title')}><div className="dash-loading">⏳ {t('merchandise.loading')}</div></Layout>;

  return (
    <Layout title={t('merchandise.title')}>
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('merchandise.title')}</h1>
          <p className="text-sm text-muted">{t('merchandise.subtitle')}</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary btn-sm"
        >
          ➕ Add Item
        </button>
      </div>

      <div className="grid-3" style={{ gap: 24 }}>
        {items.map(item => (
          <div key={item._id || item.id} className="dash-card relative group" style={{ cursor: 'pointer', padding: 0, overflow: 'hidden' }}>
            <div style={{ 
              height: 160, 
              backgroundColor: '#f9f9f9',
              backgroundImage: item.imageUrl ? `url(${resolveImage(item.imageUrl)})` : 'none', 
              backgroundSize: 'cover', 
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              {!item.imageUrl && <div style={{ fontSize: 48, opacity: 0.2 }}>🖼️</div>}
              <div style={{ position:'absolute', top:12, right:12, display:'flex', gap:6 }}>
                 <button className="btn btn-xs btn-outline bg-white" onClick={(e) => { e.stopPropagation(); handleOpenModal(item); }}>✎ Edit</button>
                 <button className="btn btn-xs btn-outline bg-white" style={{ color:'#e05252' }} onClick={(e) => { e.stopPropagation(); handleDelete(item._id || item.id); }}>✕</button>
              </div>
            </div>
            <div style={{ padding: 20 }}>
              <div className="flex-between mb-4">
                <div style={{ display: 'flex', gap: 6 }}>
                  <span className="badge badge-outline">{item.gender}</span>
                </div>
                <span className="text-sm" style={{ fontWeight: 700, color: 'var(--wine)' }}>HK$ {item.price}</span>
              </div>
              <h3 className="dash-card-title m-0 mb-2" style={{ fontSize: 18 }}>
                {tField(item.name)}
              </h3>
              {item.sizes && item.sizes.length > 0 && (
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                  <strong>Sizes:</strong> {item.sizes.map(size => typeof size === 'object' ? tField(size) : size).join(', ')}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span><strong>Stock:</strong> {item.stock || 0}</span>
                <span className={`badge ${item.inStock ? 'badge-success' : 'badge-danger'}`} style={{ padding: '2px 6px', fontSize: 10 }}>
                  {item.inStock ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="dash-empty" style={{ gridColumn: '1 / -1', padding: '60px 20px' }}>
            <div className="dash-empty-icon" style={{ fontSize: 48 }}>📦</div>
            <div className="dash-empty-title">No merchandise items added yet.</div>
            <p className="dash-empty-sub">Click "Add Item" to create your first product.</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="dash-modal-overlay">
          <div className="dash-modal" style={{ maxWidth: 500 }}>
            <div className="dash-modal-header">
              <div className="dash-modal-title">
                {editingItem ? 'Edit Merchandise' : 'Add Merchandise'}
              </div>
              <button onClick={() => setIsModalOpen(false)} className="dash-modal-close">✕</button>
            </div>
            <div className="dash-modal-body">
              {/* ✨ One-click Translate All button ✨ */}
              <div style={{ background: translated ? 'rgba(76,175,134,0.08)' : 'rgba(123,29,58,0.06)', border: `1px solid ${translated ? '#4caf86' : 'rgba(123,29,58,0.2)'}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  {translated ? (
                    <div style={{ color: '#4caf86', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 16 }}>✅</span> Bilingual content ready
                    </div>
                  ) : (
                    <div style={{ color: 'var(--wine)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 16 }}>🌐</span> Auto-Translate
                    </div>
                  )}
                  {!translated && (
                    <div style={{ fontSize: 12, color: '#888' }}>Fill name or description in either language below, then click to auto-translate.</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleTranslateAll}
                  disabled={translating}
                  style={{ background: translated ? '#4caf86' : 'var(--wine)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: translating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: translating ? 0.7 : 1, minWidth: 120 }}
                >
                  {translating ? '⏳ Translating...' : translated ? '🔄 Re-translate' : '✨ Translate All'}
                </button>
              </div>

              <div className="form-group mb-4">
                <label className="form-label-style">Item Image</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ 
                    width: 80, height: 80, borderRadius: 8, background: '#eee', 
                    backgroundImage: formData.imageUrl ? `url(${formData.photoFile ? formData.imageUrl : resolveImage(formData.imageUrl)})` : 'none', 
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {!formData.imageUrl && <span style={{ fontSize: 24, opacity: 0.5 }}>🖼️</span>}
                  </div>
                  <input type="file" accept="image/*" onChange={handlePhotoChange} />
                </div>
              </div>

              <div className="form-group mb-4">
                <label className="form-label-style">Item Name (English) *</label>
                <input
                  type="text"
                  required
                  className="form-input-style"
                  value={formData.nameEn}
                  onChange={(e) => { setFormData({ ...formData, nameEn: e.target.value }); setTranslated(false); }}
                  placeholder="e.g. Academy T-shirt"
                />
              </div>

              <div className="form-group mb-4">
                <label className="form-label-style">商品名稱 (中文 - 選填)</label>
                <input
                  type="text"
                  className="form-input-style"
                  value={formData.nameZh}
                  onChange={(e) => { setFormData({ ...formData, nameZh: e.target.value }); setTranslated(false); }}
                  placeholder="例如：學院T恤"
                />
              </div>

              <div className="form-group mb-4">
                <label className="form-label-style">{t('merchandise.description')} (English)</label>
                <textarea
                  className="form-input-style"
                  value={formData.descriptionEn}
                  onChange={(e) => { setFormData({ ...formData, descriptionEn: e.target.value }); setTranslated(false); }}
                  placeholder="Brief description of the item"
                  rows="2"
                />
              </div>

              <div className="form-group mb-4">
                <label className="form-label-style">描述 (中文 - 選填)</label>
                <textarea
                  className="form-input-style"
                  value={formData.descriptionZh}
                  onChange={(e) => { setFormData({ ...formData, descriptionZh: e.target.value }); setTranslated(false); }}
                  placeholder="例如：官方學院訓練服裝"
                  rows="2"
                />
              </div>

              <div className="form-row-2 mb-4">
                <div className="form-group mb-0">
                  <label className="form-label-style">Price (HK$) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    className="form-input-style"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="form-label-style">{t('merchandise.gender')}</label>
                  <select
                    className="form-input-style"
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  >
                    <option value="Unisex">{t('merchandise.unisex')}</option>
                    <option value="Female">{t('merchandise.female', 'Female')}</option>
                    <option value="Male">{t('merchandise.male', 'Male')}</option>
                  </select>
                </div>
              </div>

              <div className="form-row-2 mb-4">
                <div className="form-group mb-0">
                  <label className="form-label-style">Stock Quantity</label>
                  <input
                    type="number"
                    min="0"
                    className="form-input-style"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="form-group mb-0" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <label className="form-label-style">In Stock?</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <input 
                      type="checkbox" 
                      checked={formData.inStock}
                      onChange={(e) => setFormData({ ...formData, inStock: e.target.checked })}
                      style={{ width: 18, height: 18 }}
                    />
                    <span style={{ fontSize: 14 }}>Yes, item is available</span>
                  </div>
                </div>
              </div>

              <div className="form-group mb-4">
                <label className="form-label-style">Available Sizes</label>
                {Object.entries({
                  'Clothing': [
                    {en: 'XS', zh: 'XS'}, {en: 'S', zh: 'S'}, {en: 'M', zh: 'M'}, 
                    {en: 'L', zh: 'L'}, {en: 'XL', zh: 'XL'}, {en: 'XXL', zh: 'XXL'}
                  ],
                  'Shoes': [
                    {en: '4', zh: '4'}, {en: '5', zh: '5'}, {en: '6', zh: '6'}, 
                    {en: '7', zh: '7'}, {en: '8', zh: '8'}, {en: '9', zh: '9'}, 
                    {en: '10', zh: '10'}, {en: '11', zh: '11'}, {en: '12', zh: '12'}
                  ],
                  'Other': [
                    {en: 'Free Size', zh: '均碼'}, 
                    {en: 'Standard', zh: '標準'}, 
                    {en: 'N/A', zh: '不適用'}
                  ]
                }).map(([category, sizeObjs]) => (
                  <div key={category} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{category}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {sizeObjs.map(sizeObj => {
                        const isSelected = formData.sizes.some(s => 
                          (typeof s === 'string' && s === sizeObj.en) || 
                          (s.en === sizeObj.en)
                        );
                        return (
                          <div
                            key={sizeObj.en}
                            onClick={() => {
                              if (isSelected) {
                                setFormData({ 
                                  ...formData, 
                                  sizes: formData.sizes.filter(s => {
                                    if (typeof s === 'string') return s !== sizeObj.en;
                                    return s.en !== sizeObj.en;
                                  }) 
                                });
                              } else {
                                setFormData({ ...formData, sizes: [...formData.sizes, sizeObj] });
                              }
                            }}
                            style={{
                              padding: '6px 16px',
                              border: `1px solid ${isSelected ? 'var(--wine)' : '#ccc'}`,
                              backgroundColor: isSelected ? 'rgba(192, 64, 96, 0.05)' : '#fff',
                              color: isSelected ? 'var(--wine)' : '#666',
                              borderRadius: 20,
                              fontSize: 14,
                              fontWeight: isSelected ? 600 : 400,
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                          >
                            {sizeObj.en === sizeObj.zh ? sizeObj.en : `${sizeObj.en} / ${sizeObj.zh}`}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="dash-modal-footer">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="btn btn-outline btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-primary btn-sm"
              >
                {editingItem ? 'Save Changes' : 'Add Item'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
